/**
 * themeConfigs — shared theme definitions for the app.
 *
 * THEME_configs and THEME_OPTIONS were moved here verbatim from MainPage.js so
 * both desktop (MainPage) and mobile can import them without pulling in the
 * MainPage component. Pure relocation — same objects, same contents, same
 * aggregator function references; no logic change.
 *
 * Aggregator imports are unchanged except the path (../utils/... since this file
 * lives one level deeper than MainPage).
 *
 * NOTE: InternalBlurbPage.js and ReportPage.js keep their OWN, divergent copies
 * of these arrays (different defaultVars/labels) — they are intentionally NOT
 * unified here, as doing so would change their behaviour. Tech debt, tracked
 * separately.
 */

import { getPolConfig, getpolColours, getDemoPolConfig } from '../utils/polAggregators';
import { getDemoAIConfig } from '../utils/ai_aggregators';
import { getDemoGamblingConfig } from '../utils/gambling_aggregators';
import { getAIBoomConfig, getAIBoomColours } from '../utils/aiBoomAggregators';
import { getEnvironConfig, getenvironColours } from '../utils/environAggregators';
import { getHealthConfig, gethealthColours } from '../utils/healthAggregators';
import { getPriceElastConfig, getPriceElastColours } from '../utils/PriceElastAggregators';
import { getPharmaConfig, getPharmaColours } from '../utils/phrmaAggregators';
import { getPmiConfig, getPmiColours } from '../utils/pmiAggregators';
import { getSLConfig, getSLColours } from '../utils/statelegAggregators';
import { getAIConfig, getAIColours } from '../utils/ai_aggregators';
import { getACCClimateConfig, getACCClimatecolors } from '../utils/accAggregators';
import { getACCPolicyConfig, getACCPolicycolors } from '../utils/accAggregators';
import { getACCPoliticsConfig, getACCPoliticscolors } from '../utils/accAggregators';
import { getGamblingConfig, getGamblingColours } from '../utils/gambling_aggregators';
import { getIntuitConfig, getIntuitColours } from '../utils/intuitAggregators';
import { getThemeConfig, getThemeColours } from '../utils/invariantMessAggregator';
import { getHHConfig, getHHColours } from '../utils/invarianth2hAggregator';
import { getIndustryConfig, getIndustryColours } from '../utils/invariantIndustryAggregator';
import { getExampleClientConfig, getExampleClientColours } from '../utils/exampleClientAggregator';
import { getNJIConfig, getNJIColours } from '../utils/nji_aggregator';
import { getSelfDriveConfig, getSelfDriveColours } from '../utils/selfDriveAggregators';

export const THEME_configs = [
  { value: 'pol',       config: getPolConfig , defaultVar: 'econ_approve', colours: getpolColours},
  { value: 'healthcare', config: getHealthConfig, defaultVar: 'tradeoff_drugs_RnD_vs_costs', colours: gethealthColours },
  { value: 'environ',   config: getEnvironConfig , defaultVar: 'spend_climatespend', colours: getenvironColours},
  { value: 'consumer',  config: getPriceElastConfig , defaultVar: 'cheeseburgers', colours: getPriceElastColours},
  { value: 'phrma',  config: getPharmaConfig , defaultVar: 'benefits_340b', colours: getPharmaColours},
  { value: 'pmi',  config: getPmiConfig , defaultVar: 'pol_nicotine_tax', colours: getPmiColours},
  { value: 'stateleg',  config: getSLConfig , defaultVar: 'healthcare_trust', colours: getSLColours},
  { value: 'ai',  config: getAIConfig , defaultVar: 'ai_impact_life', colours: getAIColours},
  { value: 'acc_politics',  config: getACCPoliticsConfig , defaultVar: 'pol_bbb_impact', colours: getACCPoliticscolors},
  { value: 'acc_policy',  config: getACCPolicyConfig , defaultVar: 'pol_public_lands_mgmt', colours: getACCPolicycolors},
  { value: 'acc_climate',  config: getACCClimateConfig , defaultVar: 'pol_chinese_dependency', colours: getACCClimatecolors},
  { value: 'gamble',  config: getGamblingConfig , defaultVar: 'bh_gambling_last_year', colours: getGamblingColours},
  { value: 'aiboom',  config: getAIBoomConfig, defaultVar: 'ai_impact_society', colours: getAIBoomColours},
  { value: 'demo-pol',    config: getDemoPolConfig,     defaultVar: 'econ_approve',           colours: getpolColours      },
  { value: 'demo-ai',    config: getDemoAIConfig,      defaultVar: 'ai_impact_life',          colours: getAIColours       },
  { value: 'demo-gamble',config: getDemoGamblingConfig,defaultVar: 'bh_support_legal_sports_betting', colours: getGamblingColours },
  { value: 'demo-health', config: getHealthConfig, defaultVar: 'tradeoff_drugs_RnD_vs_costs', colours: gethealthColours },
  { value: 'intuit', config: getIntuitConfig, defaultVar: 'intuit_aware', colours: getIntuitColours },
  { value: 'invariant_industry', config: getIndustryConfig, defaultVar: 'industry_posneg', colours: getIndustryColours },
  { value: 'invariant_message', config: getThemeConfig, defaultVar: 'themes_convincing', colours: getThemeColours },
  { value: 'invariant_h2h', config: getHHConfig, defaultVar: 'choice_hh1', colours: getHHColours },
  { value: 'example_client', config: getExampleClientConfig, defaultVar: 'themes_convincing', colours: getExampleClientColours },
  { value: 'nji', config: getNJIConfig, defaultVar: 'nji_ad_views', colours: getNJIColours },
  { value: 'selfdrive', config: getSelfDriveConfig, defaultVar: 'self_drive_comfort', colours: getSelfDriveColours },




];
// group: 'pulse'   → National Pulse dropdown (tracking)
// group: 'monitor' → Political Monitor dropdown (single-wave / issue-specific)
// group: 'client'  → client tab (shown as their name)
export const THEME_OPTIONS = [
  { value: 'pol',               label: 'Tracking Questions', group: 'pulse'   },
  { value: 'healthcare',        label: 'Health',          group: 'monitor' },
  { value: 'environ',           label: 'Environment',     group: 'monitor' },
  { value: 'consumer',          label: 'Consumer',        group: 'monitor' },
  { value: 'ai',                label: 'Technology & AI', group: 'monitor' },
  { value: 'gamble',            label: 'Prediction Markets', group: 'monitor' },
  { value: 'aiboom',            label: 'AI & Data Centers', group: 'monitor' },
  { value: 'phrma',             label: 'Client Portal',   group: 'client'  },
  { value: 'pmi',               label: 'PMI',             group: 'client'  },
  { value: 'acc_politics',      label: 'Politics',        group: 'client'  },
  { value: 'acc_policy',        label: 'Policy',          group: 'client'  },
  { value: 'acc_climate',       label: 'Climate',         group: 'client'  },
  { value: 'intuit',            label: 'Intuit',          group: 'client'  },
  { value: 'invariant_industry',label: 'Industry Views',  group: 'client'  },
  { value: 'invariant_message', label: 'Message Testing', group: 'client'  },
  { value: 'invariant_h2h',     label: 'Head to Heads',   group: 'client'  },
  { value: 'example_client',    label: 'Example Client',  group: 'client'  },
  { value: 'demo-pol',          label: 'Tracking',        group: 'pulse'   },
  { value: 'demo-ai',           label: 'Technology & AI', group: 'monitor' },
  { value: 'demo-gamble',       label: 'Prediction Markets', group: 'monitor' },
  { value: 'demo-health',       label: 'Health',          group: 'monitor' },
  { value: 'nji',       label: 'NJI',          group: 'client'  },
  // Do not comment this out to hide AVIA from a dropdown. MainPage's
  // constructor does an unguarded THEME_OPTIONS.find(...).label, so a theme
  // that exists in THEME_configs without an entry here throws before anything
  // renders, which is what took the /highlights/selfdriving reel down.
  // To hide it from the nav instead, add it to HIDDEN_THEMES in
  // mobile/useUserThemes.js, or remove it from the client's clientAccess list.
  { value: 'selfdrive',         label: 'AVIA',            group: 'client'  },
];
